// Preview Feed — mimica o feed real das redes sociais com as pautas em gestão.
//
// 4 tabs (Instagram, LinkedIn, TikTok, YouTube), cada uma com seu formato:
//   - Instagram: grid 3xN, square 1:1
//   - TikTok:    grid 3xN, portrait 9:16
//   - YouTube:   grid 2xN, landscape 16:9
//   - LinkedIn:  lista vertical (feed cronológico)
//
// Ordenação: data_prevista DESC (post mais recente em cima, igual o IG real).
// Filtros: status (todas/aprovadas/agendado+publicado/em produção), mês.

import { useState, useMemo } from "react";
import { useQuadro } from "@/hooks/useQuadro";
import { etapasEmAndamento } from "@shared/etapas";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { PdfTile } from "./Pautas";

type Rede = "instagram" | "linkedin" | "tiktok" | "youtube";

// Specs reais do feed de cada rede (validadas no site oficial 2026-05).
// Aspect ratio + gap entre cards + largura máxima do container — tudo pra
// que o preview pareça idêntico ao feed real e não confunda revisão.
type Layout = "ig-grid" | "li-list" | "tt-grid" | "yt-grid";
const TABS: {
  value: Rede;
  label: string;
  color: string;
  bg: string;
  layout: Layout;
  aspect: string;        // CSS aspect-ratio
  gridCls: string;       // classes do container do grid/lista
  maxW: string;          // max-width do container
  cellRadius: string;    // border-radius do card
}[] = [
  // Instagram: grid 3xN, square 1:1, gap mínimo (1px) — perfil do IG é gapless
  { value: "instagram", label: "Instagram", color: "#BE185D", bg: "#FDE8F5",
    layout: "ig-grid", aspect: "1 / 1",
    gridCls: "grid grid-cols-3 gap-px", maxW: "max-w-[935px]", cellRadius: "rounded-none" },

  // LinkedIn: feed cronológico, largura ~552px no desktop
  { value: "linkedin",  label: "LinkedIn",  color: "#1E40AF", bg: "#DBEAFE",
    layout: "li-list", aspect: "1.91 / 1",
    gridCls: "space-y-2", maxW: "max-w-[552px]", cellRadius: "rounded-lg" },

  // TikTok: grid 3xN, portrait 9:16 (formato vídeo vertical), gap pequeno
  { value: "tiktok",    label: "TikTok",    color: "#065F46", bg: "#E6FBF4",
    layout: "tt-grid", aspect: "9 / 16",
    gridCls: "grid grid-cols-3 gap-1.5", maxW: "max-w-[936px]", cellRadius: "rounded-md" },

  // YouTube: grid 3xN landscape 16:9 (channel page)
  { value: "youtube",   label: "YouTube",   color: "#991B1B", bg: "#FEE2E2",
    layout: "yt-grid", aspect: "16 / 9",
    gridCls: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4", maxW: "max-w-[1280px]", cellRadius: "rounded-xl" },
];

// Rótulo e "em produção" vêm do quadro do cliente (useQuadro), out/26.
const COLUNAS_APROVADAS = new Set(["aprovado", "agendado", "publicado"]);

type Filtro = "todas" | "aprovadas" | "agendado_publicado" | "producao";

const FILTROS: { value: Filtro; label: string }[] = [
  { value: "todas",                label: "Todas em gestão" },
  { value: "producao",             label: "Em produção" },
  { value: "aprovadas",            label: "Aprovadas+" },
  { value: "agendado_publicado",   label: "Agendado + publicado" },
];

// (aspect ratio agora vem direto da TABS via tabInfo.aspect)

export default function PreviewFeed() {
  const { session } = useAuth();
  const clientId = session?.activeClientId ?? session?.clientId ?? undefined;

  const [tab, setTab]     = useState<Rede>("instagram");
  const [filtro, setFiltro] = useState<Filtro>("todas");
  const [mes, setMes]     = useState<string>("");  // "2026-06" ou "" pra todas
  const { quadro } = useQuadro(clientId ?? null);
  const COLUNAS_PRODUCAO = useMemo(() => new Set(etapasEmAndamento(quadro).map(c => c.id)), [quadro]);

  const { data: pautas = [] } = trpc.pautas.list.useQuery(
    { clientId: clientId! },
    { enabled: clientId != null }
  );
  const { data: primeirasMidias = [] } = trpc.midias.firstByClient.useQuery(
    { clientId: clientId! },
    { enabled: clientId != null }
  );

  const thumbByPauta = useMemo(() => {
    const m: Record<number, { url: string; tipo: string }> = {};
    for (const x of primeirasMidias) m[x.pautaId] = { url: x.url, tipo: x.tipo };
    return m;
  }, [primeirasMidias]);

  const mesesDisponiveis = useMemo(() => {
    const s = new Set<string>();
    for (const p of pautas) {
      if (p.dataPrevista) s.add(p.dataPrevista.slice(0, 7));  // "YYYY-MM"
    }
    return [...s].sort().reverse();
  }, [pautas]);

  const items = useMemo(() => {
    const tabInfo = TABS.find(t => t.value === tab)!;
    return pautas
      .filter(p => {
        // rede: tab atual OU "multiplas"
        if (p.rede !== tab && p.rede !== "multiplas") return false;
        // filtro status
        if (filtro === "aprovadas"          && !COLUNAS_APROVADAS.has(p.coluna)) return false;
        if (filtro === "agendado_publicado" && !(p.coluna === "agendado" || p.coluna === "publicado")) return false;
        if (filtro === "producao"           && !COLUNAS_PRODUCAO.has(p.coluna)) return false;
        // mês
        if (mes && !p.dataPrevista?.startsWith(mes)) return false;
        return true;
      })
      .sort((a, b) => {
        // mais recente primeiro (IG: top é o último postado)
        const da = a.dataPrevista ?? "9999-99-99";
        const db = b.dataPrevista ?? "9999-99-99";
        return db.localeCompare(da);
      })
      .map(p => ({ ...p, thumb: thumbByPauta[p.id] ?? null, tabInfo }));
  }, [pautas, tab, filtro, mes, thumbByPauta, COLUNAS_PRODUCAO]);

  const currentTab = TABS.find(t => t.value === tab)!;

  if (!clientId) {
    return (
      <div className="h-full flex items-center justify-center bg-[var(--solar-bg)]">
        <p className="text-[var(--solar-mute)] text-sm">Carregando…</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-[var(--solar-bg)]">
      {/* Header */}
      <div className="bg-white border-b border-[var(--solar-line)] sticky top-0 z-10">
        <div className="px-6 py-4 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-bold text-xl text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>
              Preview do Feed
            </h1>
            <p className="text-[var(--solar-mute)] text-xs mt-0.5">
              Como o feed vai ficar com as pautas em gestão · {items.length} {items.length === 1 ? "post" : "posts"}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Filtro de mês */}
            <select
              value={mes}
              onChange={e => setMes(e.target.value)}
              className="text-xs rounded-[10px] border border-[var(--solar-line)] bg-white px-3 py-1.5 text-[var(--ink-2)] shadow-[var(--elev-1)]"
            >
              <option value="">Todos os meses</option>
              {mesesDisponiveis.map(m => {
                const [y, mm] = m.split("-");
                const label = new Date(Number(y), Number(mm) - 1, 1).toLocaleDateString("pt-BR", {
                  month: "long", year: "numeric",
                });
                return <option key={m} value={m}>{label}</option>;
              })}
            </select>

            {/* Filtro de status */}
            <select
              value={filtro}
              onChange={e => setFiltro(e.target.value as Filtro)}
              className="text-xs rounded-[10px] border border-[var(--solar-line)] bg-white px-3 py-1.5 text-[var(--ink-2)] shadow-[var(--elev-1)]"
            >
              {FILTROS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Tabs por rede */}
        <div className="px-6 flex gap-1 border-t border-[var(--solar-line)]">
          {TABS.map(t => {
            const active = t.value === tab;
            const count = pautas.filter(p => p.rede === t.value || p.rede === "multiplas").length;
            return (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                className="px-4 py-3 text-xs font-semibold border-b-2 transition-colors"
                style={{
                  borderColor: active ? t.color : "transparent",
                  color: active ? t.color : "var(--solar-mute)",
                }}
              >
                {t.label}
                <span className="ml-1.5 text-[10px] opacity-60">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Conteúdo — largura do container espelha o feed real (935px IG, 552 LI) */}
      <div className={`px-4 sm:px-6 py-6 mx-auto ${currentTab.maxW}`}>
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[var(--solar-line)] bg-white p-12 text-center shadow-[var(--elev-1)]">
            <p className="text-sm font-semibold text-[var(--solar-ink)] mb-1">Nenhuma pauta {currentTab.label}</p>
            <p className="text-xs text-[var(--solar-mute)]">
              {filtro !== "todas" || mes
                ? "Tente outro filtro ou outro mês."
                : "Cadastra uma pauta com essa rede pra ela aparecer aqui."}
            </p>
          </div>
        ) : currentTab.layout === "li-list" ? (
          <div className={currentTab.gridCls}>
            {items.map(p => (
              <LinkedInPost key={p.id} pauta={p} thumb={p.thumb} />
            ))}
          </div>
        ) : (
          <div className={currentTab.gridCls}>
            {items.map(p => (
              <GridCell
                key={p.id}
                pauta={p}
                thumb={p.thumb}
                aspect={currentTab.aspect}
                tabColor={currentTab.color}
                tabBg={currentTab.bg}
                cellRadius={currentTab.cellRadius}
                showTitleUnderCell={currentTab.layout === "yt-grid"}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Célula do grid (IG / TikTok / YouTube) ──────────────────────────────────
function GridCell({
  pauta, thumb, aspect, tabColor, tabBg, cellRadius, showTitleUnderCell,
}: {
  pauta: any;
  thumb: { url: string; tipo: string } | null;
  aspect: string;
  tabColor: string;
  tabBg: string;
  cellRadius: string;
  showTitleUnderCell: boolean;  // YouTube mostra título abaixo do thumb (como o feed real)
}) {
  const { rotulo } = useQuadro();
  const isVideo = thumb?.tipo === "video";
  const dataLabel = pauta.dataPrevista
    ? new Date(pauta.dataPrevista + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
    : "";

  const thumbBlock = (
    <a
      href={`/pautas?focus=${pauta.id}`}
      className={`group relative block overflow-hidden bg-[var(--solar-bg)] hover:opacity-95 transition-opacity ${cellRadius}`}
      style={{ aspectRatio: aspect }}
      title={pauta.titulo}
    >
      {thumb?.url ? (
        thumb.tipo === "pdf" ? (
          <PdfTile nome={pauta.titulo} />
        ) : isVideo ? (
          <video src={thumb.url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
        ) : (
          <img src={thumb.url} alt={pauta.titulo} className="w-full h-full object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = "0.3"; }} />
        )
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-3 text-center" style={{ background: tabBg }}>
          <p className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: tabColor }}>
            {pauta.formato}
          </p>
          <p className="text-[11px] font-semibold mt-1 line-clamp-3" style={{ color: tabColor }}>
            {pauta.titulo}
          </p>
        </div>
      )}

      {/* Badges sobrepostas (top-right) */}
      <div className="absolute top-1.5 right-1.5 flex flex-col items-end gap-1">
        {pauta.formato === "carrossel" && (
          <span className="bg-black/60 text-white text-[9px] px-1 py-0.5 rounded font-semibold">⊞</span>
        )}
        {isVideo && (
          <span className="bg-black/60 text-white text-[9px] px-1 py-0.5 rounded font-semibold">▶</span>
        )}
      </div>

      {/* Footer hover c/ status + data */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-2">
        <p className="text-[10px] text-white font-semibold uppercase tracking-wider">
          {rotulo(pauta.coluna)} {dataLabel && `· ${dataLabel}`}
        </p>
        <p className="text-[11px] text-white line-clamp-2 mt-0.5">{pauta.titulo}</p>
      </div>
    </a>
  );

  // YouTube real exibe título + canal abaixo da thumb. Sem isso seria só uma
  // grade de imagens, longe da experiência real do feed do YT.
  if (!showTitleUnderCell) return thumbBlock;
  return (
    <div className="flex flex-col">
      {thumbBlock}
      <div className="pt-2 px-0.5">
        <p className="text-sm font-medium text-[var(--solar-ink)] line-clamp-2 leading-snug">{pauta.titulo}</p>
        <p className="text-xs text-[var(--solar-mute)] mt-1">
          {rotulo(pauta.coluna)} {dataLabel && `· ${dataLabel}`}
        </p>
      </div>
    </div>
  );
}

// ─── Post estilo LinkedIn ────────────────────────────────────────────────────
function LinkedInPost({ pauta, thumb }: { pauta: any; thumb: { url: string; tipo: string } | null }) {
  const { rotulo } = useQuadro();
  const dataLabel = pauta.dataPrevista
    ? new Date(pauta.dataPrevista + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
    : "sem data";

  return (
    <a href={`/pautas?focus=${pauta.id}`}
      className="block bg-white rounded-xl border border-[var(--solar-line)] overflow-hidden shadow-[var(--elev-1)] hover:shadow-[var(--elev-2)] transition-shadow">
      <div className="px-5 py-4 flex items-center gap-3 border-b border-[var(--solar-line)]">
        <div className="w-10 h-10 rounded-full bg-[#1E40AF] text-white flex items-center justify-center text-sm font-bold">
          C
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[var(--solar-ink)]">Cliente · {dataLabel}</p>
          <p className="text-[11px] text-[var(--solar-mute)]">
            {rotulo(pauta.coluna)} · {pauta.formato}
          </p>
        </div>
      </div>

      <div className="px-5 py-3">
        <p className="text-sm text-[var(--solar-ink)] line-clamp-3">{pauta.titulo}</p>
        {pauta.legenda && (
          <p className="text-xs text-[var(--ink-2)] mt-1 line-clamp-2">{pauta.legenda}</p>
        )}
      </div>

      {thumb?.url && (
        <div className="bg-[var(--solar-bg)]" style={{ aspectRatio: "1.91 / 1" }}>
          {thumb.tipo === "pdf" ? (
            <PdfTile nome="Documento (PDF)" />
          ) : thumb.tipo === "video" ? (
            <video src={thumb.url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
          ) : (
            <img src={thumb.url} alt={pauta.titulo} className="w-full h-full object-cover" />
          )}
        </div>
      )}

      <div className="px-5 py-2 text-xs text-[var(--solar-mute)] border-t border-[var(--solar-line)]">
        pré-visualização
      </div>
    </a>
  );
}
